import { useEffect, useState } from "react";
import { billingSubscription, setToken, type BillingSubscription } from "../lib/api";

// Shown when a company's access is paused (402 company_suspended). For a
// self-serve subscription that ended, the owner can renew the same plan right
// here — the clients, reports and letters are all still saved.
export default function SuspendedPage() {
  const [sub, setSub] = useState<BillingSubscription | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const billingParam = new URLSearchParams(window.location.search).get("billing");
  const renewCancelled = billingParam === "renew_cancelled";
  // Back from a paid renewal: Stripe's webhook unlocks the account a few
  // seconds later. Wait for it here (never show Renew again meanwhile, so
  // nobody pays twice), then go straight back into the app.
  const [finishing, setFinishing] = useState(billingParam === "renewed");
  const [slow, setSlow] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    billingSubscription.get().then((s) => { if (!cancelled) setSub(s); }).catch(() => { /* fall back to the generic message */ });
    return () => { cancelled = true; };
  }, []);

  const [checkNow, setCheckNow] = useState(0);
  useEffect(() => {
    if (!finishing) return;
    let cancelled = false; let tries = 0; let timer = 0;
    const tick = async () => {
      tries += 1;
      try {
        const s = await billingSubscription.get();
        if (cancelled) return;
        setSub(s);
        if (!s.suspended) { window.location.href = "/settings?billing=renewed"; return; }
        // The bank/card payment failed: stop waiting and let the owner try again.
        if (s.renewal?.status === "failed") { setFinishing(false); setPaymentFailed(true); return; }
      } catch { /* keep waiting */ }
      if (tries === 15) setSlow(true);
      // Keep checking for as long as the page is open: every 3 s for the first
      // minute, then backing off to every 30 s. Renew stays hidden throughout.
      if (!cancelled) timer = window.setTimeout(tick, tries < 20 ? 3000 : 30000);
    };
    tick();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [finishing, checkNow]);

  function signOut() {
    // Clear any stored staff token, then return to the sign-in screen.
    setToken(null);
    window.location.href = "/login";
  }

  async function renew() {
    setBusy(true); setErr(null);
    try {
      const r = await billingSubscription.renew();
      window.location.assign(r.url);
    } catch (e: any) {
      setErr(e?.message || "Couldn't start the renewal. Try again in a minute.");
      setBusy(false);
    }
  }

  const ended = !!sub?.has_subscription && !!sub?.suspended;

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="brand-lockup login-brand">
          <div className="brand-mark" aria-hidden="true">
            <img src="/brand-logo.png" alt="" />
          </div>
          <div>
            <strong className="brand-wordmark">No Excuses</strong>
            <span>Dispute Manager</span>
          </div>
        </div>
        <h1 id="suspended-heading">{finishing ? "Finishing your renewal…" : ended ? "Your subscription has ended" : "Access paused"}</h1>
        <div role="status" aria-live="polite">
          {finishing ? (
            <p>
              Thanks — we're confirming your payment and turning your account back on. This usually takes a few seconds.
              {slow && " It's taking longer than usual; this page keeps checking. You won't be charged again."}
            </p>
          ) : null}
          {finishing && slow && (
            <p><button type="button" className="ghost-button" onClick={() => setCheckNow((n) => n + 1)}>Check again now</button></p>
          )}
          {finishing ? null : ended ? (
            <>
              <p>
                Your account is paused{sub?.plan ? ` (${sub.plan.label} plan)` : ""}. Everything is still saved —
                your clients, reports and letters are right where you left them.
              </p>
              <p>
                {sub?.renewable
                  ? "Renew to pick up where you left off. Have the code COMEBACK20? Enter it at checkout for 20% off your first 3 months."
                  : "Ask the account owner to sign in and renew the subscription."}
              </p>
            </>
          ) : (
            <>
              <p>
                This company&rsquo;s access to the Dispute Manager is currently paused.
                Your work is safe&mdash;nothing has been deleted.
              </p>
              <p>
                Please contact your administrator to restore access. If you believe this
                is a mistake, reach out and they can reactivate the account.
              </p>
            </>
          )}
          {renewCancelled && <p className="muted small">Checkout was cancelled — nothing was charged.</p>}
          {(paymentFailed || (!finishing && sub?.renewal?.status === "failed")) && (
            <p className="error-text" role="alert">Your renewal payment didn't go through, so the account is still paused. You can try again with a different card.</p>
          )}
          {err && <p className="error-text" role="alert">{err}</p>}
        </div>
        <div className="stacked-form" aria-labelledby="suspended-heading">
          {!finishing && ended && sub?.renewable && (
            <button type="button" className="primary-button wide-button" onClick={renew} disabled={busy} data-testid="renew-subscription">
              {busy ? "Opening secure checkout…" : "Renew my subscription"}
            </button>
          )}
          <button type="button" className={!finishing && ended && sub?.renewable ? "ghost-button wide-button" : "primary-button wide-button"} onClick={signOut}>
            Sign out
          </button>
          {!(ended && sub?.renewable) && (
            <a href="/login" className="ghost-button wide-button">Back to sign in</a>
          )}
        </div>
      </div>
    </div>
  );
}
